import Seo from "@/components/Seo";
import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, ExternalLink, Flame } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/Shared";
import CrossSellBanner from "@/components/CrossSellBanner";

const CATS = ["All", "Models", "Research", "Industry", "Funding", "Regulation", "Safety", "Products"];
const SOURCES = ["All Sources", "OpenAI", "Google DeepMind", "Anthropic", "Microsoft", "Hugging Face",
  "TechCrunch", "MIT Technology Review", "VentureBeat", "The Verge", "Wired"];

const CAT_COLORS = {
  Models: "bg-[#8B5CF6]/15 text-[#a78bfa] border-[#8B5CF6]/30",
  Research: "bg-[#3B82F6]/15 text-[#60a5fa] border-[#3B82F6]/30",
  Industry: "bg-[#14B8A6]/15 text-[#2dd4bf] border-[#14B8A6]/30",
  Funding: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30",
  Regulation: "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30",
  Safety: "bg-[#EF4444]/15 text-[#f87171] border-[#EF4444]/30",
  Products: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/30",
};

const CAT_GRADIENTS = {
  Models: "bg-gradient-to-br from-[#7C3AED] via-[#8B5CF6] to-[#4C1D95]",
  Research: "bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#1E3A8A]",
  Industry: "bg-gradient-to-br from-[#0D9488] via-[#14B8A6] to-[#134E4A]",
  Funding: "bg-gradient-to-br from-[#16A34A] via-[#22C55E] to-[#14532D]",
  Regulation: "bg-gradient-to-br from-[#D97706] via-[#F59E0B] to-[#78350F]",
  Safety: "bg-gradient-to-br from-[#DC2626] via-[#EF4444] to-[#7F1D1D]",
  Products: "bg-gradient-to-br from-[#0891B2] via-[#00F0FF] to-[#083344]",
};

const relTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) { const h = Math.floor(diff / 3600); return `${h} hour${h > 1 ? "s" : ""} ago`; }
  const d = Math.floor(diff / 86400);
  return `${d} day${d > 1 ? "s" : ""} ago`;
};

const favicon = (sourceUrl) => {
  try { return `https://www.google.com/s2/favicons?domain=${new URL(sourceUrl).hostname}&sz=32`; }
  catch { return null; }
};

const CatTag = ({ category }) => (
  <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${CAT_COLORS[category] || CAT_COLORS.Industry}`}>
    {category}
  </span>
);

const HotBadge = () => (
  <span className="flex items-center gap-1 font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#EF4444]/20 text-[#f87171] border border-[#EF4444]/40">
    <Flame className="w-3 h-3" /> Hot
  </span>
);

const SourceLine = ({ a }) => (
  <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
    {favicon(a.source_url) && <img src={favicon(a.source_url)} alt="" className="w-4 h-4 rounded" />}
    <span>{a.source_name}</span>
    <span>·</span>
    <span>{relTime(a.published_at)}</span>
  </div>
);

const ArticleImage = ({ a, className, textSize = "text-xl" }) => {
  const [failed, setFailed] = useState(false);
  const showImg = a.image_url && !failed;
  const grad = CAT_GRADIENTS[a.category] || CAT_GRADIENTS.Industry;
  return (
    <div className={`overflow-hidden relative shrink-0 ${className}`}>
      {showImg ? (
        <img src={a.image_url} alt={a.title} className="w-full h-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <div className={`w-full h-full flex items-center justify-center relative ${grad}`}>
          <div className="absolute inset-0 bg-black/25" />
          <span className={`relative font-display font-bold ${textSize} text-white/90 text-center px-4 drop-shadow`}>{a.source_name}</span>
        </div>
      )}
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <CatTag category={a.category} />
        {a.is_hot && <HotBadge />}
      </div>
    </div>
  );
};

const StandardCard = ({ a, i }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: Math.min(i, 9) * 0.03 }}
    className="card-surface rounded-xl overflow-hidden flex flex-col" data-testid={`article-card-${a.id}`}>
    <ArticleImage a={a} className="h-40" textSize="text-xl" />
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-display font-bold text-base leading-snug line-clamp-2 mb-2">{a.title}</h3>
      <p className="text-sm text-[#94A3B8] line-clamp-3 flex-1">{a.summary || "Read the full article for details."}</p>
      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
        <SourceLine a={a} />
        <a href={a.article_url} target="_blank" rel="noreferrer" data-testid={`article-read-${a.id}`}
          className="text-xs text-[#00F0FF] flex items-center gap-1 hover:gap-1.5 transition-all shrink-0">
          Read <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  </motion.div>
);

const HeroCard = ({ a }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
    className="card-surface featured-glow rounded-xl overflow-hidden md:col-span-2 lg:col-span-3 flex flex-col md:flex-row" data-testid={`article-hero-${a.id}`}>
    <ArticleImage a={a} className="md:w-1/2 h-56 md:h-auto" textSize="text-3xl" />
    <div className="p-7 md:w-1/2 flex flex-col justify-center">
      <span className="font-mono-tech text-xs uppercase text-[#00F0FF] mb-3">Latest</span>
      <h2 className="font-display font-extrabold text-2xl lg:text-3xl leading-tight mb-3">{a.title}</h2>
      <p className="text-[#94A3B8] line-clamp-3 mb-5">{a.summary || "Read the full article for details."}</p>
      <div className="flex items-center justify-between gap-3">
        <SourceLine a={a} />
        <a href={a.article_url} target="_blank" rel="noreferrer" data-testid={`article-read-${a.id}`}
          className="text-sm bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition flex items-center gap-1.5 shrink-0">
          Read Full Article <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  </motion.div>
);

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setDone(true); setEmail("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="md:col-span-2 lg:col-span-3 rounded-xl bg-[#0e1424] border border-[#00F0FF]/20 p-10 text-center grid-bg" data-testid="newsletter-section">
      <h3 className="font-display font-extrabold text-3xl mb-2">Get the WorldAI Weekly</h3>
      <p className="text-[#94A3B8] mb-6">Top AI news, new tools, and job picks — every Friday morning.</p>
      {done ? (
        <p className="text-[#10B981] font-semibold" data-testid="newsletter-success">You're in — first issue arrives Friday 🎉</p>
      ) : (
        <form onSubmit={submit} className="flex gap-2 max-w-md mx-auto">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="newsletter-email"
            className="flex-1 bg-[#0B0F19] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#00F0FF] focus:outline-none" />
          <button disabled={busy} data-testid="newsletter-submit"
            className="bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50">
            {busy ? "…" : "Subscribe"}
          </button>
        </form>
      )}
      <p className="text-xs text-[#64748B] mt-4">A growing community of AI professionals — free forever.</p>
    </div>
  );
};

export default function News() {
  const [data, setData] = useState({ articles: [], total: 0, last_updated: null, ticker: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [cat, setCat] = useState("All");
  const [source, setSource] = useState("All Sources");
  const [sort, setSort] = useState("latest");

  const load = () => {
    setLoading(true);
    api.get("/articles", { params: { search, category: cat, source, sort } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, source, sort]);

  const ticker = data.ticker || [];
  const tickerLoop = useMemo(() => [...ticker, ...ticker], [ticker]);
  const articles = data.articles || [];
  const hero = useMemo(() => {
    if (!articles.length) return null;
    return articles.find((a) => a.is_featured) ||
      articles.reduce((a, b) => (new Date(a.published_at) >= new Date(b.published_at) ? a : b));
  }, [articles]);
  const rest = useMemo(() => articles.filter((a) => a.id !== hero?.id), [articles, hero]);

  return (
    <div data-testid="news-page">
      <Seo title="AI News — Latest AI Headlines, Research & Funding | WorldAIInfo" description="The latest AI news from top sources — model launches, research, funding, regulation and product updates. Aggregated and updated hourly. Free." canonicalPath="/news" />
      {/* Ticker */}
      {ticker.length > 0 && (
        <div className="bg-[#060911] border-b border-white/10 overflow-hidden py-2.5" data-testid="news-ticker">
          <div className="ticker-track">
            {tickerLoop.map((t, i) => (
              <a key={i} href={t.article_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 text-sm text-[#94A3B8] hover:text-[#00F0FF] transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
                {t.title}
              </a>
            ))}
          </div>
        </div>
      )}

      <PageHeader overline="Live Feed" title="AI News"
        subtitle="Live updates from 10 global AI sources — refreshed every 2 hours."
        action={
          <div className="flex flex-col items-start md:items-end gap-2">
            <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
              <span className="w-2 h-2 rounded-full bg-[#10B981] live-dot" />
              Last updated {data.last_updated ? relTime(data.last_updated) : "—"}
            </div>
            <span className="font-mono-tech text-xs border border-white/10 rounded px-3 py-1 text-[#94A3B8]" data-testid="news-total-count">
              {data.total} articles
            </span>
          </div>
        } />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Filters */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search AI news..." data-testid="news-search"
                className="w-full bg-[#111827] border border-white/10 rounded-md pl-10 pr-4 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" />
            </div>
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="news-source"
              className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none">
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="news-sort"
              className="bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm text-[#94A3B8] focus:border-[#00F0FF] focus:outline-none">
              <option value="latest">Latest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`news-cat-${c.toLowerCase()}`}
                className={`font-mono-tech text-xs px-3 py-2 rounded border transition ${cat === c ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#94A3B8] hover:border-white/25"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Empty text="Loading news…" /> : articles.length === 0 ? <Empty text="No articles found." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hero && <HeroCard key={hero.id} a={hero} />}
            {rest.map((a, i) => {
              const nodes = [<StandardCard key={a.id} a={a} i={i} />];
              if (i === 5) nodes.push(<Newsletter key="newsletter" />);
              return nodes;
            })}
          </div>
        )}

        <p className="text-xs text-[#64748B] leading-relaxed mt-16 pt-8 border-t border-white/10" data-testid="news-disclaimer">
          All news is sourced automatically from official publications including OpenAI, Google DeepMind, Anthropic,
          TechCrunch, MIT Technology Review, VentureBeat, The Verge, and Wired. Clicking any headline opens the original
          article on the publisher's website. WorldAIInfo.com does not own or produce this content.
        </p>
      </div>
      <CrossSellBanner />
    </div>
  );
}
